<template>
  <div class="mr-3 v-theme--light">
    <h6 class="fs-6 fw-bold mt-4 mb-4 opacity-75">4CH</h6>
    <div class="row mb-2 py-0">
      <div class="col" v-if="!isOperationOffice">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSideOn')"
          v-model="form.cams_on"
          :items="selectCameraOn"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_on'))"
          :error-messages="errors[errorKeyPrefix + '.cams_on']"
        />
      </div>
      <div class="col">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSidePixel')"
          v-model="form.cams_pixel"
          :items="selectCameraPixel"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_pixel')) || isInstallationOffice"
          :error-messages="errors[errorKeyPrefix + '.cams_pixel']"
        />
      </div>
      <div class="col" v-if="isOperationOffice"></div>
    </div>
    <div class="row mb-2 py-0" v-if="!isOperationOffice">
      <div class="col">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSideBitrate')"
          v-model="form.cams_bitrate"
          :items="selectCameraBitrate"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_bitrate')) || isInstallationOffice"
          :error-messages="errors[errorKeyPrefix + '.cams_bitrate']"
        />
      </div>
      <div class="col">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSideHorizon')"
          v-model="form.cams_h"
          :items="selectCameraHorizon"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_h')) || isInstallationOffice"
          :error-messages="errors[errorKeyPrefix + '.cams_h']"
        />
      </div>
    </div>
    <div class="row mb-2 py-0" v-if="!isOperationOffice">
      <div class="col">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSideFps')"
          v-model="form.cams_fps"
          :items="selectFps"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_fps')) || isInstallationOffice"
          :error-messages="errors[errorKeyPrefix + '.cams_fps']"
        />
      </div>
      <div class="col">
        <v-select
          hide-details="auto"
          class="small mt-2"
          :label="i18n.global.t('pages.vehicles.form.iniFileSetting.record.cameraSideVertical')"
          v-model="form.cams_v"
          :items="selectCameraVertical"
          :clearable="false"
          :disabled="(selectDefault && readonlyInputs.includes('cams_v')) || isInstallationOffice"
          :error-messages="errors[errorKeyPrefix + '.cams_v']"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import i18n from '@/lang/i18n';

const props = withDefaults(defineProps<{
  modelValue: Record<string, any>,
  formInputs: Record<string, any>,
  selectDefault: boolean,
  isOperationOffice: boolean,
  isInstallationOffice: boolean,
  errors: Record<string, string[]>,
}>(), {
  selectDefault: false,
  isOperationOffice: false,
  isInstallationOffice: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void;
}>();

// Reactive data
const readonlyInputs = ref<string[]>([]);
const errorKeyPrefix = 'initial_params.record';

// Select options
const selectCameraPixel = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectPixelFHD'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectPixelHD'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectPixelSD360'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectPixelSD480'),
];

const selectCameraOn = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectCameraOnOn'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectCameraOnOff')
];

const selectCameraBitrate = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectBitrateHigh'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectBitrateMeddle'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectBitrateLow'),
];

const selectCameraHorizon = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectHorizonNormal'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectHorizonMirror'),
];

const selectCameraVertical = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectVerticalNormal'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectVerticalRotate'),
];

const selectFps = [
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectFps5'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectFps10'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectFps15'),
  i18n.global.t('pages.vehicles.form.iniFileSetting.record.selectFps30'),
];

// Computed properties
const form = computed({
  get() {
    return props.modelValue || {};
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

// Watchers
watch(() => props.formInputs, () => {
  if (!props.formInputs?.form?.record) {
    return;
  }
  const key = Object.keys(props.formInputs.default_params)[0];
  readonlyInputs.value = Object.keys(
    props.formInputs.default_params[key]?.parameters?.record ?? []
  );
});
</script>
